import React, { useEffect, useState, useRef } from "react"
import { validateModuleMatch } from "./utils"
import { initialModuleState } from "../initialModuleState"

/**
 * # Module Database Hook (useModuleDB)
 * 
 * This hook provides comprehensive module data management, handling the loading,
 * validation, and synchronization of module data between base templates and user records.
 * 
 * ## Core Functionality
 * 
 * ### Data Loading Strategy
 * The hook manages two primary data sources:
 * - **Base Module Template**: The original module created by educators
 * - **User Module Record**: Student's personalized copy with responses and progress
 * 
 * ### Loading Logic
 * 1. Attempts to load user's existing module record
 * 2. Falls back to base template if no user record exists
 * 3. Validates compatibility between user record and current template
 * 4. Handles module restart if template has diverged significantly
 * 
 * ## Key Features
 * 
 * ### Intelligent Data Resolution
 * - **Student Mode**: Loads user record with responses and progress
 * - **Preview Mode**: Uses base template without user-specific data
 * - **Educator Review**: Can load other users' records for assessment
 * 
 * ### Template Validation
 * - Compares user record structure with current base template
 * - Detects incompatible changes that would break user progress
 * - Provides restart mechanism for corrupted or outdated records
 * 
 * ### Performance Optimization
 * - Ref-based change detection to prevent unnecessary re-loads
 * - Conditional loading based on interaction requirements
 * - Efficient state management with minimal re-renders
 * 
 * ### Error Handling
 * - Comprehensive error states for failed loads
 * - Graceful fallbacks for missing or corrupted data
 * - User-friendly error messages and recovery options
 * 
 * ## Parameters
 * 
 * - **moduleId**: SQL database ID of the module to load
 * - **firebase**: Firebase service instance for API calls
 * - **isModuleInteractive**: Boolean flag for interactive vs preview mode
 * - **otherUserFirebaseUserId**: Optional - for loading another user's record
 * 
 * ## Return Values
 * 
 * - **userModuleData**: Current module data (user record or base template)
 * - **isModuleLoading**: Loading state indicator
 * - **error**: Error state for failed operations
 * - **baseModuleTemplate**: Original module template for reference
 * - **loadUserModuleRecord**: Function to manually reload module data
 * - **isModulePrivateAndAuthoredByUser**: Permission check for private modules
 * 
 * ## Usage Patterns
 * 
 * ### Student Taking Module
 * ```javascript
 * const { userModuleData, isModuleLoading } = useModuleDB(
 *   moduleId, 
 *   firebase, 
 *   true // interactive mode
 * );
 * ```
 * 
 * ### Educator Preview
 * ```javascript
 * const { userModuleData, baseModuleTemplate } = useModuleDB(
 *   moduleId, 
 *   firebase, 
 *   false // preview mode
 * );
 * ```
 * 
 * ### Assessment Review
 * ```javascript
 * const { userModuleData } = useModuleDB(
 *   moduleId, 
 *   firebase, 
 *   true, 
 *   studentFirebaseUserId // view student's responses
 * );
 * ```
 * 
 * ## Integration Points
 * 
 * - **InteractiveModule.js**: Primary consumer for student experience
 * - **ModuleCreationPage.js**: Uses for editing existing modules
 * - **Assessment systems**: Loads student submissions for review
 * - **Progress tracking**: Provides completion and response data
 * 
 * ## Data Validation
 * 
 * The hook includes sophisticated validation logic:
 * - Structure comparison between user record and base template
 * - Detection of breaking changes in module structure
 * - Automatic restart mechanism for incompatible versions
 * - Preservation of student progress when possible
 * 
 * ## Security & Privacy
 * 
 * - Role-based access control for private modules
 * - User ownership validation for sensitive operations
 * - Secure handling of student response data
 * - Privacy protection for cross-user record access
 * 
 * For related documentation:
 * - Module state management: ModuleProvider.js
 * - Interactive features: InteractiveModule.js
 * - Component system: MODULE_COMPONENT_SYSTEM.md
 */
/**
 * Fetches module data from SQL database including user module record and base module template.
 * @returns
 */
const useModuleDB = (moduleId, firebase, isModuleInteractive, otherUserFirebaseUserId) => {
  const [userModuleData, setUserModuleData] = useState(null)
  const [isModuleLoading, setIsModuleLoading] = useState(!!moduleId) // Loading is false if no moduleId
  const [error, setError] = useState(null)
  // pull module template and user record, can compare and restart if they have diverged
  const [baseModuleTemplate, setBaseModuleTemplate] = useState(null)
  const [hasLoadedInitially, setHasLoadedInitially] = useState(false)
  const [userSQLId, setUserSQLId] = useState(null)

  const prevModuleIdRef = useRef() // Ref to store the previous value of moduleId
  const prevFirebaseRef = useRef() // Ref to store the previous value of firebase

  const isModulePrivateAndAuthoredByUser = `${userSQLId}` === baseModuleTemplate?.creator_id && baseModuleTemplate?.access_type === "private"

  const loadUserModuleRecord = async () => {
    const res = await firebase?.getUserModuleRecord(moduleId, otherUserFirebaseUserId)
    const resUserModuleRecord = res?.data?.userModuleRecord
    const resBaseModuleTemplate = res?.data?.baseModuleTemplate
    const userSQLId = res?.data?.callerSQLUserId
    setUserSQLId(userSQLId)

    setBaseModuleTemplate(resBaseModuleTemplate)

    if (resBaseModuleTemplate?.module_json && resUserModuleRecord?.module_json) {
      // validation in order to restart the module if it has diverged from the created template, required to keep things synced
      const isValid = validateModuleMatch(resUserModuleRecord.module_json.sections, resBaseModuleTemplate.module_json.sections)

      // disabled validation, this is a temporary fix to allow users to continue working on their modules 

      // if (!isValid && isModuleInteractive && resUserModuleRecord.status === "in_progress") {
      //   await firebase.restartUserModule(moduleId)
      //   alert("Sorry, your module has diverged from the base template. We have restarted it for you.")
      //   if (typeof window !== "undefined") {
      //     window.location.reload()
      //   }
      // }
      // we have a user module record
      setUserModuleData(resUserModuleRecord)

      return resUserModuleRecord
    } else {
      // we don't have a user module record, so we use the base template
      setUserModuleData(resBaseModuleTemplate)
      return resBaseModuleTemplate
    }
  }

  const initializeModule = async () => {
    const res = await firebase?.getUserModuleRecord(moduleId, otherUserFirebaseUserId)

    const resUserModuleRecord = res?.data?.userModuleRecord
    const resBaseModuleTemplate = res?.data?.baseModuleTemplate
    const userSQLId = res?.data?.callerSQLUserId
    setUserSQLId(userSQLId)

    setBaseModuleTemplate(resBaseModuleTemplate)

    if (resBaseModuleTemplate.module_json && resUserModuleRecord?.module_json) {
      // validation in order to restart the module if it has diverged from the created template, required to keep things synced
      const isValid = validateModuleMatch(resUserModuleRecord.module_json.sections, resBaseModuleTemplate.module_json.sections)
      if (!isValid && isModuleInteractive && resUserModuleRecord.status === "in_progress") {

        // disabled validation, this is a temporary fix to stop restarting modules

        // await firebase.restartUserModule(moduleId)
        // alert("Sorry, your module has diverged from the base template. We have restarted it for you.")
        // if (typeof window !== "undefined") {
        //   window.location.reload()
        // }
      }
      setUserModuleData(resUserModuleRecord)
    } else {
      setUserModuleData(resBaseModuleTemplate)
    }

    if (!resUserModuleRecord && isModuleInteractive) {
      // we don't have a user module record, so we create one
      await firebase?.createUserModule(moduleId)
    }
  }

  useEffect(() => {
    // // Access previous values
    // const prevModuleId = prevModuleIdRef.current
    // const prevFirebase = prevFirebaseRef.current


    // // Update refs with current values
    // prevModuleIdRef.current = moduleId
    // prevFirebaseRef.current = firebase

    const fetchData = async () => {
      try {
        if (hasLoadedInitially) {
          return
        }
        await initializeModule()
        setHasLoadedInitially(true)
      } catch (err) {
        console.error("Error fetching module data:", err)
      } finally {
        setIsModuleLoading(false) // Set loading to false after fetching data
      }
    }

    if (!!firebase) {
      fetchData()
    }
  }, [moduleId, firebase]) // Include firebase in the dependency array

  return { userModuleData, isModuleLoading, loadUserModuleRecord, error, baseModuleTemplate, userSQLId, isModulePrivateAndAuthoredByUser }
}

export default useModuleDB
